import { Maximize2, Minimize2 } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import MarkdownEditor from '../Common/MarkdownEditor';
import Editor from '@monaco-editor/react';
import mermaid from 'mermaid';
import debounce from 'lodash/debounce';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import { useCadrageStore } from '../../stores/cadrageStore';
import { useLayoutStore } from '../../stores/layoutStore';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

interface DatabaseSchemaSectionProps {
  isReadOnly?: boolean; // Mode lecture seule
}

const DatabaseSchemaSection: React.FC<DatabaseSchemaSectionProps> = ({ isReadOnly = false }) => {
  const { t } = useTranslation();
  const { isFullscreen, toggleFullscreen } = useLayoutStore();
  const databaseSchemaNotesMd = useCadrageStore((state) => state.databaseSchemaNotesMd);
  const updateDatabaseSchemaNotesMd = useCadrageStore((state) => state.updateDatabaseSchemaNotesMd);
  const mermaidSchema = useCadrageStore((state) => state.mermaidSchema);
  const updateMermaidSchema = useCadrageStore((state) => state.updateMermaidSchema);
  const saveSchemaAndNotes = useCadrageStore((state) => state.saveSchemaAndNotes);
  const isLoading = useCadrageStore((state) => state.isLoading);
  const cadrageError = useCadrageStore((state) => state.error);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mermaidRenderError, setMermaidRenderError] = useState<string | null>(null);

  useEffect(() => {
    try {
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'loose',
        theme: 'default',
      });
    } catch (e) {
      console.error(t('databaseSchema.initError'), e);
    }
  }, [t]);

  const renderMermaidDiagram = useCallback(async (code: string) => {
    const container = document.getElementById('mermaid-diagram-container');
    if (!container) {
      console.warn(t('databaseSchema.containerNotFound'));
      return;
    }

    if (!code || code.trim() === '') {
      container.innerHTML =
        `<p class="text-gray-400 italic text-center mt-4">${t('databaseSchema.emptyMessage')}</p>`;
      setMermaidRenderError(null);
      return;
    }

    try {
      const { svg } = await mermaid.render('graphDivInternalId', code);
      container.innerHTML = svg;
      setMermaidRenderError(null);
    } catch (e: any) {
      console.error(t('databaseSchema.renderErrorLog'), e.message);
      setMermaidRenderError(e.message || t('databaseSchema.unknownRenderError'));
    }
  }, [t]);

  const debouncedRenderMermaid = useCallback(debounce(renderMermaidDiagram, 600), [renderMermaidDiagram]);

  useEffect(() => {
    if (mermaidSchema !== undefined) {
      debouncedRenderMermaid(mermaidSchema);
    }
    return () => {
      debouncedRenderMermaid.cancel();
    };
  }, [mermaidSchema, debouncedRenderMermaid, t]);

  if (isLoading && mermaidSchema === undefined) {
    return <div>{t('databaseSchema.loading')}</div>;
  }

  const handleNotesChange = (value: string | undefined) => {
    updateDatabaseSchemaNotesMd(value || '');
  };

  const handleSaveSchemaAndNotesClick = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveSchemaAndNotes();
    } catch (err) {
      setSaveError(
        err instanceof Error
          ? err.message
          : t('databaseSchema.unknownSaveError'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn(
      "database-schema-section p-6 bg-card rounded-lg shadow-sm border border-border space-y-6",
      isFullscreen && "fixed inset-0 z-50 rounded-none overflow-y-auto"
    )}>
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-card-foreground">
          {t('databaseSchema.title')}
        </h2>
        <div className="flex items-center space-x-2">
          {/* Bouton Plein écran */}
          <button
            onClick={toggleFullscreen}
            className="inline-flex items-center px-3 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title={isFullscreen ? t('global.exitFullscreen', 'Quitter le plein écran') : t('global.fullscreen', 'Plein écran')}
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="h-4 w-4 mr-1" /> {t('global.exitFullscreen', 'Quitter')}
              </>
            ) : (
              <>
                <Maximize2 className="h-4 w-4 mr-1" /> {t('global.fullscreen', 'Plein écran')}
              </>
            )}
          </button>
          {!isReadOnly && (
            <button
              onClick={handleSaveSchemaAndNotesClick}
              disabled={isSaving || isLoading}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
            >
              {isSaving ? t('global.saving') : t('databaseSchema.saveButton')}
            </button>
          )}
        </div>
      </div>
      {saveError && (
        <p className="text-destructive text-sm">
          {t('databaseSchema.saveErrorPrefix')} {saveError}
        </p>
      )}
      {cadrageError && (
        <p className="text-destructive text-sm">
          {t('databaseSchema.storeErrorPrefix')} {cadrageError}
        </p>
      )}

      <div className={cn(
        "flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4",
        isFullscreen ? "h-[calc(100vh-250px)]" : "h-[calc(100vh-300px)]"
      )}>
        {/* Hide editor in read-only mode */}
        {!isReadOnly && (
          <div className="w-full md:w-1/2 flex flex-col">
            <label htmlFor="mermaid-editor" className="block text-sm font-medium text-foreground mb-1">
              {t('databaseSchema.codeLabel')}
            </label>
            <div className="flex-grow border border-border rounded-md overflow-hidden">
              <Editor
                height="100%"
                language="markdown"
                theme="vs-light"
                value={mermaidSchema || ''}
                onChange={(value) => updateMermaidSchema(value || '')}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                }}
              />
            </div>
          </div>
        )}

        <div className={cn(
          "p-4 border border-border rounded bg-muted overflow-auto flex flex-col relative",
          isReadOnly ? "w-full" : "w-full md:w-1/2"
        )}>
          <h3 className="text-lg font-medium text-card-foreground mb-2">
            {t('databaseSchema.diagramTitle')}
          </h3>
          {mermaidRenderError && (
            <p className="text-destructive text-sm mb-2">
              {t('databaseSchema.renderErrorPrefix')} {mermaidRenderError}
            </p>
          )}
          <TransformWrapper
            initialScale={1}
            minScale={0.2}
            maxScale={3}
            limitToBounds={false}
            doubleClick={{ disabled: true }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <div className="tools absolute top-2 right-2 z-10 space-x-1">
                  <button
                    onClick={() => zoomIn()}
                    className="p-1 bg-background border border-border rounded hover:bg-accent text-foreground text-sm"
                  >
                    {t('databaseSchema.zoomIn')}
                  </button>
                  <button
                    onClick={() => zoomOut()}
                    className="p-1 bg-background border border-border rounded hover:bg-accent text-foreground text-sm"
                  >
                    {t('databaseSchema.zoomOut')}
                  </button>
                  <button
                    onClick={() => resetTransform()}
                    className="p-1 bg-background border border-border rounded hover:bg-accent text-foreground text-sm"
                  >
                    {t('databaseSchema.reset')}
                  </button>
                </div>
                <TransformComponent
                  wrapperStyle={{ width: '100%', height: '100%' }}
                  contentStyle={{ width: '100%', height: '100%' }}
                >
                  <div id="mermaid-diagram-container" className="mermaid w-full h-full" />
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-foreground mb-1">
          {t('databaseSchema.notesLabel')}
        </label>
        <MarkdownEditor
          value={databaseSchemaNotesMd}
          onChange={isReadOnly ? undefined : handleNotesChange}
          height={200}
          disabled={isReadOnly}
        />
      </div>
    </div>
  );
};

export default DatabaseSchemaSection;
